import {
    exportDocumentPdf,
    signDocumentPdf,
    SignPdfResponse,
} from '@/lib/api/medical/signature-api'
import { getFileUrl } from '@/lib/utils/file-url'
import { useAuth } from '@/store/hooks'
import { useState } from 'react'
import { MdDownload, MdLock, MdPictureAsPdf, MdVerified } from 'react-icons/md'
import { toast } from 'react-toastify'

interface SignDocumentModalProps {
    isOpen: boolean
    onClose: () => void
    documentType: string
    documentId: number
    documentTitle: string
    onSigned?: (result: SignPdfResponse) => void
    onExported?: () => void
}

export function SignDocumentModal({
    isOpen,
    onClose,
    documentType,
    documentId,
    documentTitle,
    onSigned,
    onExported,
}: SignDocumentModalProps) {
    const { user } = useAuth()
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [skipSign, setSkipSign] = useState(false)
    const [result, setResult] = useState<SignPdfResponse | null>(null)

    if (!isOpen) return null

    const handleSign = async () => {
        if (!skipSign && !password.trim()) {
            toast.error('Informe a senha do certificado')
            return
        }

        setLoading(true)
        try {
            if (skipSign) {
                await exportDocumentPdf({
                    documentType,
                    documentId,
                    companyId: user?.companyId || 0,
                })
                toast.success('PDF exportado com sucesso!')
                onExported?.()
                handleClose()
            } else {
                const response = await signDocumentPdf({
                    documentType,
                    documentId,
                    certificatePassword: password,
                    companyId: user?.companyId || 0,
                })

                if (response.success && response.result) {
                    setResult(response.result)
                    toast.success('Documento assinado com sucesso!')
                    onSigned?.(response.result)
                } else {
                    toast.error(
                        response.error || 'Erro ao assinar documento'
                    )
                }
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    (skipSign
                        ? 'Erro ao exportar PDF'
                        : 'Erro ao assinar documento')
            )
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setPassword('')
        setResult(null)
        setSkipSign(false)
        onClose()
    }


    return (
        <div className="modal modal-open">
            <div className="modal-box !max-w-md">
                <h3 className="text-lg font-bold mb-4">
                    {skipSign ? 'Exportar PDF' : 'Assinar Documento Digitalmente'}
                </h3>

                {!result ? (
                    <>
                        <div className="space-y-4">
                            <div className="bg-base-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-base-content/70">
                                    Documento
                                </p>
                                <p className="font-medium">
                                    {documentTitle}
                                </p>
                                <p className="text-xs text-base-content/50 mt-1">
                                    Tipo: {documentType} | ID:{' '}
                                    {documentId}
                                </p>
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-base-300 hover:bg-base-200/50 transition-colors">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-secondary"
                                    checked={skipSign}
                                    onChange={(e) => {
                                        setSkipSign(e.target.checked)
                                        if (e.target.checked) {
                                            setPassword('')
                                        }
                                    }}
                                    disabled={loading}
                                />
                                <div>
                                    <span className="text-sm font-medium">
                                        Exportar sem assinar
                                    </span>
                                    <p className="text-xs text-base-content/50">
                                        Gera o PDF sem assinatura digital para exportação rápida
                                    </p>
                                </div>
                            </label>

                            {!skipSign && (
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Senha do Certificado A1
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="password"
                                            className="input input-bordered w-full pr-10"
                                            placeholder="Digite a senha do seu certificado"
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(
                                                    e.target.value
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter')
                                                    handleSign()
                                            }}
                                            disabled={loading}
                                            autoFocus
                                        />
                                        <MdLock className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30" />
                                    </div>
                                    <label className="label">
                                        <span className="label-text-alt text-base-content/50">
                                            A senha é necessária para
                                            acessar sua chave privada
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleSign}
                                disabled={
                                    loading || (!skipSign && !password.trim())
                                }
                            >
                                {loading ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : skipSign ? (
                                    <MdPictureAsPdf className="text-lg" />
                                ) : (
                                    <MdLock className="text-lg" />
                                )}
                                {loading
                                    ? skipSign
                                        ? 'Exportando...'
                                        : 'Assinando...'
                                    : skipSign
                                        ? 'Exportar PDF'
                                        : 'Assinar Digitalmente'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                                <MdVerified className="text-3xl text-green-600 shrink-0" />
                                <div>
                                    <p className="font-bold text-green-700 dark:text-green-400">
                                        Documento assinado com
                                        sucesso!
                                    </p>
                                    <p className="text-sm text-green-600 dark:text-green-500">
                                        Assinatura digital válida
                                    </p>
                                </div>
                            </div>

                            <div className="bg-base-200 rounded-lg p-3 space-y-1 text-sm">
                                <p>
                                    <span className="font-medium">
                                        Certificado:
                                    </span>{' '}
                                    {
                                        result.certificate
                                            .serialNumber
                                    }
                                </p>
                                <p>
                                    <span className="font-medium">
                                        Emissor:
                                    </span>{' '}
                                    {result.certificate.issuer}
                                </p>
                                <p>
                                    <span className="font-medium">
                                        Validade:
                                    </span>{' '}
                                    {new Date(
                                        result.certificate.validFrom
                                    ).toLocaleDateString('pt-BR')}{' '}
                                    a{' '}
                                    {new Date(
                                        result.certificate.validUntil
                                    ).toLocaleDateString('pt-BR')}
                                </p>
                            </div>
                        </div>

                        <div className="modal-action">
                            <a
                                href={getFileUrl(result.signedPdfUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                            >
                                <MdDownload className="text-lg" />
                                Baixar PDF Assinado
                            </a>
                            <button
                                className="btn btn-ghost"
                                onClick={handleClose}
                            >
                                Fechar
                            </button>
                        </div>
                    </>
                )}
            </div>
            <div
                className="modal-backdrop"
                onClick={handleClose}
            />
        </div>
    )
}
